import Button from '@ingka/button';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import crossIcon from '@ingka/ssr-icon/paths/cross';
import {
  CHECKIN,
  REGISTERED,
  SELF_CHECKIN,
  WAITING_LIST,
} from '../Register/enums';
import CancelRegistrationModal from './CancelRegistrationModal';
import { TimeSlot } from '../../types/model';
import {
  isTimeSlotExpired,
  isCheckedIn,
  isRegistrationClosed,
  toTimestamp,
} from '../../utils';

type PropsType = {
  registrationState: string;
  timeSlot: TimeSlot | null | undefined;
  disabled?: boolean;
};

type CopyType = {
  [key: string]: {
    button: string;
  };
};

const CancelButton = styled(Button)`
  margin-top: var(--space-300);
`;

const CANCEL_REGISTRATION_TRIGGER_ID = 'cancel-registration-trigger';

const CancelRegistration = ({ registrationState, timeSlot, disabled = false }: PropsType) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const triggerButtonRef = useRef<HTMLElement | null>(null);
  const { t } = useTranslation();
  const isExpired = timeSlot
    ? isTimeSlotExpired(timeSlot) &&
      isRegistrationClosed(
        toTimestamp(timeSlot.utcRegistrationClosedDate as number)
      )
    : false;
  const isCheckedInStatus = timeSlot ? isCheckedIn(timeSlot) : false;
  const isCancelDisabled = isExpired || isCheckedInStatus;

  const openCancelModal = (event: MouseEvent<HTMLButtonElement>) => {
    triggerButtonRef.current = event.currentTarget as HTMLElement;
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);

    const focusTrigger = () => {
      const trigger =
        triggerButtonRef.current ||
        (document.getElementById(CANCEL_REGISTRATION_TRIGGER_ID) as HTMLElement | null);
      trigger?.focus();
    };

    setTimeout(() => {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(focusTrigger);
      } else {
        focusTrigger();
      }
    }, 0);
  };

  useEffect(() => {
    const appRoot = document.getElementById('root');
    if (!appRoot) return;

    if (showCancelModal) {
      appRoot.setAttribute('aria-hidden', 'true');
      appRoot.setAttribute('inert', '');
    } else {
      appRoot.removeAttribute('aria-hidden');
      appRoot.removeAttribute('inert');
    }

    return () => {
      appRoot.removeAttribute('aria-hidden');
      appRoot.removeAttribute('inert');
    };
  }, [showCancelModal]);

  const copy: CopyType = {
    [REGISTERED]: {
      button: 'event.cancelRegistration',
    },
    [CHECKIN]: {
      button: 'event.cancelRegistration',
    },
    [SELF_CHECKIN]: {
      button: 'event.cancelRegistration',
    },
    [WAITING_LIST]: {
      button: 'event.leaveWaitingList',
    },
  };

  return (
    <>
      <CancelButton
        id={CANCEL_REGISTRATION_TRIGGER_ID}
        type="secondary"
        text={t(copy[registrationState].button)}
        ssrIcon={crossIcon}
        onClick={openCancelModal}
        disabled={isCancelDisabled || disabled}
      />
      {!isCancelDisabled &&
        createPortal(
          <CancelRegistrationModal
            visible={showCancelModal}
            closeModal={closeCancelModal}
            registrationState={registrationState}
          />,
          document.body
        )}
    </>
  );
};

export default CancelRegistration;
